import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useEvent } from '../contexts/EventContext';
import DashCard, { DashCardPrintStyles } from '../components/DashCard';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Check } from 'lucide-react';

export default function PrintDashCards() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentEvent } = useEvent();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCars, setSelectedCars] = useState([]);
  const [showPrintView, setShowPrintView] = useState(false);

  // Get car IDs from URL params (comma-separated)
  const carIdsParam = searchParams.get('ids');
  const printAll = searchParams.get('all') === 'true';

  useEffect(() => {
    if (currentEvent) {
      fetchCars();
    }
  }, [currentEvent, carIdsParam, printAll]);

  const fetchCars = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/cars?event_id=${currentEvent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let carList = response.data || [];
      
      // Filter to specific cars if IDs provided
      if (carIdsParam) {
        const ids = carIdsParam.split(',');
        carList = carList.filter(car => ids.includes(car.id));
      }
      
      // If print all approved, filter to approved only
      if (printAll) {
        carList = carList.filter(car => car.approved);
      }
      
      // Sort by registration number
      carList.sort((a, b) => a.registration_number.localeCompare(b.registration_number));
      
      setCars(carList);
      setSelectedCars(carList.map(c => c.id));
    } catch (error) {
      console.error('Error fetching cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCarSelection = (carId) => {
    if (selectedCars.includes(carId)) {
      setSelectedCars(selectedCars.filter(id => id !== carId));
    } else {
      setSelectedCars([...selectedCars, carId]);
    }
  };

  const selectAll = () => {
    setSelectedCars(cars.map(c => c.id));
  };

  const deselectAll = () => {
    setSelectedCars([]);
  };

  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const baseUrl = window.location.origin;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  // Print view - only shows selected dash cards
  if (showPrintView) {
    const carsToPrint = cars.filter(c => selectedCars.includes(c.id));
    
    return (
      <div className="bg-white min-h-screen">
        <DashCardPrintStyles />
        
        {/* No-print controls */}
        <div className="no-print fixed top-4 left-1/2 transform -translate-x-1/2 z-50 flex gap-2 bg-white p-4 rounded-lg shadow-xl border border-gray-200">
          <button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                window.print();
              }, 100);
            }}
            className="inline-flex items-center px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white font-medium rounded-md"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Now
          </button>
          <button 
            type="button"
            onClick={() => setShowPrintView(false)}
            className="inline-flex items-center px-4 py-2 bg-white text-gray-900 hover:bg-gray-100 font-medium rounded-md border border-gray-300"
          >
            Back to Selection
          </button>
        </div>

        {/* Print area */}
        <div className="print-area">
          {carsToPrint.map(car => (
            <DashCard 
              key={car.id}
              car={car}
              eventName={currentEvent?.name || 'Car Show'}
              baseUrl={baseUrl}
            />
          ))}
        </div>
      </div>
    );
  }

  // Selection view
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <DashCardPrintStyles />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-gray-700 hover:text-pink-600 hover:bg-pink-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-700">
              {selectedCars.length} of {cars.length} selected
            </span>
            <Button 
              onClick={handlePrint}
              disabled={selectedCars.length === 0}
              className="bg-pink-600 hover:bg-pink-700"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Selected ({selectedCars.length})
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Print Dash Cards</h1>
          <p className="text-gray-700">Select which cars to print dash cards for</p>
        </div>

        {/* Selection Controls */}
        <div className="flex gap-4 mb-6">
          <Button variant="outline" onClick={selectAll} className="border-gray-300 text-gray-700 hover:bg-gray-50">
            Select All
          </Button>
          <Button variant="outline" onClick={deselectAll} className="border-gray-300 text-gray-700 hover:bg-gray-50">
            Deselect All
          </Button>
        </div>

        {/* Car Grid */}
        {cars.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center shadow-sm">
            <p className="text-xl text-gray-700">No cars found</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cars.map(car => (
              <div
                key={car.id}
                onClick={() => toggleCarSelection(car.id)}
                className={`bg-white border rounded-lg p-4 cursor-pointer transition-all shadow-sm ${
                  selectedCars.includes(car.id)
                    ? 'ring-2 ring-pink-500 border-pink-300 bg-pink-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                      selectedCars.includes(car.id)
                        ? 'bg-pink-500 border-pink-500'
                        : 'border-gray-400'
                    }`}>
                      {selectedCars.includes(car.id) && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div>
                      <span className="text-2xl font-bold text-gray-900">#{car.registration_number}</span>
                      <span className={`ml-2 text-xs px-2 py-1 rounded ${
                        car.approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {car.approved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-2 ml-9">
                  <p className="text-gray-900 font-medium">
                    {car.year} {car.make} {car.model}
                  </p>
                  <p className="text-gray-700 text-sm">{car.owner_name}</p>
                  <p className="text-gray-600 text-xs">{car.class_name}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Preview Section */}
        {selectedCars.length > 0 && cars.find(c => c.id === selectedCars[0]) && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Preview</h2>
            <p className="text-gray-700 mb-6">First selected card preview (actual print will be on white background)</p>
            
            <div className="bg-white rounded-lg p-4 inline-block shadow-xl border border-gray-200">
              <DashCard 
                car={cars.find(c => c.id === selectedCars[0])}
                eventName={currentEvent?.name || 'Car Show'}
                baseUrl={baseUrl}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
